import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Paper,
  Chip,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Divider,
  CircularProgress,
  Skeleton,
  LinearProgress,
  IconButton,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TimerIcon from '@mui/icons-material/Timer';
import CancelIcon from '@mui/icons-material/Cancel';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import Navbar from '../components/Navbar/Navbar';
import ListeningTestUI from '../components/IELTS/ListeningTestUI';
import ReadingTestUI from '../components/IELTS/ReadingTestUI';
import WritingTestUI from '../components/IELTS/WritingTestUI';
import SpeakingTestUI from '../components/IELTS/SpeakingTestUI';
import IELTSBandScore from '../components/IELTS/IELTSBandScore';
import EndModal from '../components/MockTest/Common/EndModal';
import { Base_URL } from '../Client/apiURL';
import { shouldShowScore, getBandScore, getAIScoring, hasAIScore } from '../components/IELTS/utils/scoreHelpers';

// IELTS Full Test messages for EndModal
const IELTS_FULL_TEST_SENTENCES = [
  "Processing all test sections...",
  "Analyzing Listening responses...",
  "Evaluating Reading comprehension...",
  "Scoring Writing tasks...",
  "Reviewing Speaking performance...",
  "Finalizing full test results..."
];

const PageContainer = styled(Container)(({ theme }) => ({
  paddingTop: '120px',
  paddingBottom: '2rem',
  minHeight: '100vh',
  [theme.breakpoints.down('md')]: {
    paddingTop: '100px',
    paddingLeft: '1rem',
    paddingRight: '1rem',
  },
}));

const TestHeader = styled(Paper)(({ theme }) => ({
  padding: '24px',
  marginBottom: '24px',
  borderRadius: '12px',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
    marginBottom: '16px',
  },
}));

const SectionCard = styled(Paper)(({ theme, completed, active }) => ({
  padding: '14px',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  border: active ? '2px solid #996CFE' : completed ? '2px solid #4CAF50' : '1px solid #E0E0E0',
  backgroundColor: active ? '#F5F0FF' : completed ? '#E8F5E8' : '#FFFFFF',
  // cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '12px',
  },
}));

const StartButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#996CFE',
  color: 'white',
  borderRadius: '8px',
  padding: '12px 24px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#7C4DFF',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 20px',
    fontSize: '14px',
  },
}));

// Helper to count correct/incorrect answers from analytics
function getCorrectIncorrectCounts(analytics) {
  let correct = 0, incorrect = 0;
  if (analytics) {
    Object.values(analytics).forEach(a => {
      if (a.isCorrect) correct++;
      else incorrect++;
    });
  }
  return { correct, incorrect };
}

/** When a group has many test_id rows per category, pick the row that has real content / attempts for resume. */
function pickResumeTestRowForCategory(testRows, categoryName) {
  const want = String(categoryName || '').toLowerCase();
  const rows = (testRows || []).filter(
    (t) => String(t.category_name || '').toLowerCase() === want
  );
  if (!rows.length) return null;

  const scoreRow = (r) => {
    const nAttempts = Array.isArray(r.attempts_summary) ? r.attempts_summary.length : 0;
    const nSecs = Array.isArray(r.sections) ? r.sections.length : 0;
    const nQuestions = Array.isArray(r.sections)
      ? r.sections.reduce((acc, s) => acc + (Array.isArray(s.questions) ? s.questions.length : 0), 0)
      : 0;
    const hasBody = Array.isArray(r.sections) && r.sections.some(
      (s) =>
        (s.passage_html && String(s.passage_html).trim()) ||
        (s.passage_text && String(s.passage_text).trim()) ||
        (Array.isArray(s.questions) && s.questions.length > 0)
    );
    return { nAttempts, hasBody: hasBody ? 1 : 0, nSecs, nQuestions };
  };

  rows.sort((a, b) => {
    const sa = scoreRow(a);
    const sb = scoreRow(b);
    if (sb.nAttempts !== sa.nAttempts) return sb.nAttempts - sa.nAttempts;
    if (sb.hasBody !== sa.hasBody) return sb.hasBody - sa.hasBody;
    if (sb.nQuestions !== sa.nQuestions) return sb.nQuestions - sa.nQuestions;
    if (sb.nSecs !== sa.nSecs) return sb.nSecs - sa.nSecs;
    const tb = new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
    if (tb !== 0) return tb;
    return (parseInt(b.id, 10) || 0) - (parseInt(a.id, 10) || 0);
  });
  return rows[0];
}

/** Resolve attempt id from a resume-context payload (shape varies slightly after JSON / older APIs). */
function getPrefetchFullAttemptId(prefetched) {
  if (!prefetched || typeof prefetched !== 'object') return null;
  const top = Number(
    prefetched.fullTestAttemptId ??
      prefetched.full_test_attempt_id ??
      prefetched.fullTestAttempt?.id ??
      prefetched.fullTestAttempt?.full_test_attempt_id
  );
  if (Number.isFinite(top) && top > 0) return top;
  const tests = prefetched.tests;
  if (!Array.isArray(tests)) return null;
  for (const t of tests) {
    const arr = t?.attempts_summary;
    if (!Array.isArray(arr)) continue;
    for (const a of arr) {
      const n = Number(a?.fullTestAttemptId ?? a?.full_test_attempt_id);
      if (Number.isFinite(n) && n > 0) return n;
    }
  }
  return null;
}

/** Typical `{ responseCode, message, response }` bodies from `/app/users/ielts-tests/*` routes. */
function isIeltsApiSuccessEnvelope(body) {
  if (body == null || typeof body !== 'object') return false;
  const code = body.responseCode;
  if (code === undefined || code === null) return body.response != null;
  return Number(code) === 200;
}

/** Map resume-context payload + dashboard groupedTests into resume-screen state. */
function deriveResumeUiStateFromPayload(payload, groupedTests) {
  const sectionOrder = ['Listening', 'Reading', 'Writing', 'Speaking'];
  const sortedAll = [...groupedTests].sort((a, b) => {
    const aIndex = sectionOrder.indexOf(a.category_name);
    const bIndex = sectionOrder.indexOf(b.category_name);
    return aIndex - bIndex;
  });
  const speakingOptionalMode = !!payload.isSpeakingOptional;
  const filtered = speakingOptionalMode
    ? sortedAll.filter((t) => t.category_name !== 'Speaking')
    : sortedAll;

  const statusByCategory = {};
  (payload.tests || []).forEach((bt) => {
    const key = String(bt.category_name || '').toLowerCase();
    if (key) {
      const existing = statusByCategory[key];
      const incoming = bt.category_attempt_status;
      if (existing === 'completed' || incoming === 'completed') {
        statusByCategory[key] = 'completed';
      } else if (!existing) {
        statusByCategory[key] = incoming;
      }
    }
  });
  const completedIdx = [];
  filtered.forEach((t, idx) => {
    const key = String(t.category_name || '').toLowerCase();
    if (statusByCategory[key] === 'completed') {
      completedIdx.push(idx);
    }
  });

  const overrides = {};
  filtered.forEach((t) => {
    const best = pickResumeTestRowForCategory(payload.tests, t.category_name);
    if (!best) return;
    const bestSecs = Array.isArray(best.sections) ? best.sections.length : 0;
    const baseSecs = Array.isArray(t.sections) ? t.sections.length : 0;
    const idDiff = String(best.id) !== String(t.id);
    const payloadRicher = bestSecs > baseSecs;
    if (idDiff || payloadRicher) {
      overrides[t.category_name] = {
        id: best.id,
        sections: best.sections?.length ? best.sections : t.sections || [],
        audio_url: best.audio_url ?? t.audio_url,
        answer_sheet: best.answer_sheet ?? t.answer_sheet,
        explaination: best.explaination ?? t.explaination,
      };
    }
  });

  let nextIdx = filtered.findIndex((t) => {
    const key = String(t.category_name || '').toLowerCase();
    return statusByCategory[key] !== 'completed';
  });
  if (nextIdx < 0) nextIdx = 0;

  const resolvedAttemptId = getPrefetchFullAttemptId(payload) || 0;

  return {
    fullTestAttemptId: resolvedAttemptId,
    includeSpeaking: !speakingOptionalMode,
    completedSections: completedIdx,
    resumeTestOverrides: Object.keys(overrides).length ? overrides : null,
    resumeNextSectionIndex: nextIdx,
  };
}

// Section result summary box (local, for full test results only)
function SectionResultSummary({ result }) {
  if (!result || !result.response) return null;
  const { analytics } = result.response;
  
  // Use helper functions to get band score (handles 0 correctly)
  const bandScore = getBandScore(result);
  const aiScoring = getAIScoring(result);
  
  // Only show correct/incorrect for Listening/Reading
  const { correct, incorrect } = getCorrectIncorrectCounts(analytics);
  const showCorrectIncorrect = correct !== undefined && incorrect !== undefined;
  
  return (
    <>
      <Box sx={{
        width: '100%',
        maxWidth: 600,
        mx: 'auto',
        mt: 2,
        mb: 3,
        p: 3,
        borderRadius: 3,
        boxShadow: '0 4px 24px rgba(153,108,254,0.10)',
        background: 'linear-gradient(90deg, #F8F2FF 60%, #E8DFFF 100%)',
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 3,
      }}>
        <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#996CFE', mb: 1 }}>
            🎉 Your Band: {bandScore !== null ? bandScore : 'N/A'}
          </Typography>
        </Box>
        {showCorrectIncorrect && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: { xs: 2, sm: 0 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon sx={{ color: '#37965E', fontSize: 32 }} />
              <Typography variant="h6" sx={{ color: '#37965E', fontWeight: 700 }}>
                {correct} Correct
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CancelIcon sx={{ color: '#E53935', fontSize: 32 }} />
              <Typography variant="h6" sx={{ color: '#E53935', fontWeight: 700 }}>
                {incorrect} Incorrect
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      {analytics && (
        <Box mt={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Question Breakdown
          </Typography>
          {Object.entries(analytics).map(([qNum, qData]) => (
            <Box
              key={qNum}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                mb: 1,
                p: 1,
                borderRadius: 2,
                background: qData.isCorrect ? '#E8F5E9' : '#FFF3F3',
                border: `1px solid ${qData.isCorrect ? '#37965E' : '#E53935'}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ minWidth: 40, fontWeight: 700, color: '#996CFE' }}>
                  Q{qNum}
                </Typography>
                <Typography sx={{ flex: 1, ml: 2 }}>
                  Your answer: <b>{qData.userAnswer ?? '-'}</b>
                </Typography>
                {qData.isCorrect ? (
                  <CheckCircleIcon sx={{ color: '#37965E', ml: 2 }} />
                ) : (
                  <CancelIcon sx={{ color: '#E53935', ml: 2 }} />
                )}
              </Box>
              {!qData.isCorrect && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, ml: 7 }}>
                  <Typography sx={{ color: '#37965E', fontSize: 15 }}>
                    Correct: <b>{qData.correctAnswer ?? '-'}</b>
                  </Typography>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </>
  );
}

const IELTSFullTestPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const {
    groupedTests,
    groupId,
    mode,
    continuePrevious,
    completionStatus,
    resumeFullTestAttemptId: stateResumeFullId,
    fullTestAttemptId: stateFullAttemptId,
    prefetchedResumeContext,
  } = location.state || {};

  const resumeFlowAttemptId =
    stateResumeFullId ||
    (continuePrevious && completionStatus?.resumeFullTestAttemptId) ||
    null;
  const isResumeFlow =
    resumeFlowAttemptId != null &&
    resumeFlowAttemptId !== '' &&
    String(resumeFlowAttemptId).trim() !== '';

  const prefetchAttemptId = getPrefetchFullAttemptId(prefetchedResumeContext);
  const prefetchedResumeMatchesAttempt = Boolean(
    prefetchedResumeContext &&
      Array.isArray(prefetchedResumeContext.tests) &&
      prefetchedResumeContext.tests.length > 0 &&
      resumeFlowAttemptId != null &&
      String(resumeFlowAttemptId).trim() !== '' &&
      prefetchAttemptId === Number(resumeFlowAttemptId)
  );
  
  const [currentSectionIndex, setCurrentSectionIndex] = useState(-1); // -1 means overview page
  const [completedSections, setCompletedSections] = useState([]);
  const [testStarted, setTestStarted] = useState(false);
  const [testResults, setTestResults] = useState({});
  const [fullTestAttemptId, setFullTestAttemptId] = useState(
    stateFullAttemptId != null && stateFullAttemptId !== '' ? Number(stateFullAttemptId) : null
  );
  const [isResumeMode, setIsResumeMode] = useState(false);
  const [resumeNextSectionIndex, setResumeNextSectionIndex] = useState(null);
  const [resumeContextLoaded, setResumeContextLoaded] = useState(false);
  const [resumeContextLoading, setResumeContextLoading] = useState(
    isResumeFlow && !prefetchedResumeMatchesAttempt
  );
  /** Merge canonical test row from resume-context (per category) into dashboard ids. */
  const [resumeTestOverrides, setResumeTestOverrides] = useState(null);
  const [includeSpeaking, setIncludeSpeaking] = useState(true);
  const [showSpeakingOption, setShowSpeakingOption] = useState(false);
  const [showFullTestResults, setShowFullTestResults] = useState(false);
  const [completingFullTest, setCompletingFullTest] = useState(false);
  const [testsWithDetails, setTestsWithDetails] = useState({}); // Cache for test details
  const [loadingTestDetails, setLoadingTestDetails] = useState(false);
  /** When resume-context only, groupedTests can carry summary `sections` — gate real UI on full getTestById cache. */
  const [sectionDetailLoadError, setSectionDetailLoadError] = useState(null);
  const [endModalOpen, setEndModalOpen] = useState(false);
  const [submittedSections, setSubmittedSections] = useState(new Set()); // Track which sections have started submission
  const [fullTestPauseDialogOpen, setFullTestPauseDialogOpen] = useState(false);
  const fullTestFinalizeTimerRef = useRef(null);

  // Sort tests by category order (Listening, Reading, Writing, Speaking)
  const sectionOrder = ['Listening', 'Reading', 'Writing', 'Speaking'];
  const allSortedTests = useMemo(() => {
    if (!groupedTests?.length) return [];
    const base = [...groupedTests].sort((a, b) => {
      const aIndex = sectionOrder.indexOf(a.category_name);
      const bIndex = sectionOrder.indexOf(b.category_name);
      return aIndex - bIndex;
    });
    if (!resumeTestOverrides) return base;
    return base.map((t) => {
      const o = resumeTestOverrides[t.category_name];
      return o ? { ...t, ...o } : t;
    });
  }, [groupedTests, resumeTestOverrides]);

  // Filter tests based on speaking inclusion
  const sortedTests = includeSpeaking 
    ? allSortedTests 
    : allSortedTests.filter(test => test.category_name !== 'Speaking');

  // Fetch test details for a specific test
  const fetchTestDetails = useCallback(async (testId) => {
    setSectionDetailLoadError((prev) => (prev?.testId === testId ? null : prev));
    setLoadingTestDetails(true);
    try {
      const response = await fetch(`${Base_URL}/app/users/ielts-tests/getTestById/${testId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const payload = data.response;
      if (
        payload &&
        Array.isArray(payload.sections) &&
        payload.sections.length > 0
      ) {
        setTestsWithDetails(prev => {
          if (prev[testId]) {
            return prev;
          }
          return {
            ...prev,
            [testId]: payload
          };
        });
        setSectionDetailLoadError((prev) => (prev?.testId === testId ? null : prev));
        return payload;
      }
      throw new Error(data?.message || 'This section could not be loaded.');
    } catch (error) {
      console.error('Error fetching test details:', error);
      setSectionDetailLoadError({
        testId,
        message: error.message || 'Could not load this section. Please try again.',
      });
      return null;
    } finally {
      setLoadingTestDetails(false);
    }
  }, []);

  useLayoutEffect(() => {
    if (!groupedTests || !groupId) return;
    const resumeId =
      stateResumeFullId ||
      (continuePrevious && completionStatus?.resumeFullTestAttemptId) ||
      null;
    if (!resumeId || !prefetchedResumeMatchesAttempt || !prefetchedResumeContext) return;

    const d = deriveResumeUiStateFromPayload(prefetchedResumeContext, groupedTests);
    setFullTestAttemptId(d.fullTestAttemptId);
    setIncludeSpeaking(d.includeSpeaking);
    setIsResumeMode(true);
    setCompletedSections(d.completedSections);
    setResumeTestOverrides(d.resumeTestOverrides);
    setResumeNextSectionIndex(d.resumeNextSectionIndex);
    setTestStarted(false);
    setCurrentSectionIndex(-1);
    setResumeContextLoaded(true);
    setResumeContextLoading(false);
  }, [
    groupedTests,
    groupId,
    prefetchedResumeContext,
    prefetchedResumeMatchesAttempt,
    stateResumeFullId,
    continuePrevious,
    completionStatus,
  ]);

  useEffect(() => {
    if (!groupedTests || !groupId) {
      navigate('/ielts-dashboard');
      return;
    }

    const resumeId =
      stateResumeFullId ||
      (continuePrevious && completionStatus?.resumeFullTestAttemptId) ||
      null;

    if (!resumeId) {
      setIsResumeMode(false);
      setResumeNextSectionIndex(null);
      setResumeContextLoaded(false);
      setResumeContextLoading(false);
      setResumeTestOverrides(null);
      if (continuePrevious && completionStatus?.attempted) {
        const completedSectionNames = Object.keys(completionStatus.attempted).filter(
          (section) => completionStatus.attempted[section]
        );
        const completedIndices = [];
        const legacySorted = [...(groupedTests || [])].sort((a, b) => {
          const aIndex = sectionOrder.indexOf(a.category_name);
          const bIndex = sectionOrder.indexOf(b.category_name);
          return aIndex - bIndex;
        });
        legacySorted.forEach((test, index) => {
          if (completedSectionNames.includes(test.category_name.toLowerCase())) {
            completedIndices.push(index);
          }
        });
        setIncludeSpeaking(true);
        setCompletedSections(completedIndices);
        const nextSectionIndex = legacySorted.findIndex(
          (test) => !completedSectionNames.includes(test.category_name.toLowerCase())
        );
        if (nextSectionIndex !== -1) {
          setTestStarted(true);
          setCurrentSectionIndex(nextSectionIndex);
        } else {
          setShowFullTestResults(true);
        }
      }
      return;
    }

    if (prefetchedResumeMatchesAttempt && prefetchedResumeContext) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setResumeTestOverrides(null);
        setResumeContextLoading(true);
        const response = await fetch(
          `${Base_URL}/app/users/ielts-tests/full-attempts/${resumeId}/resume-context`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
          }
        );
        const data = await response.json();
        if (cancelled || data.responseCode !== 200 || !data.response) {
          if (!cancelled) {
            setResumeContextLoaded(true);
          }
          return;
        }
        const payload = data.response;
        const d = deriveResumeUiStateFromPayload(payload, groupedTests);
        setFullTestAttemptId(d.fullTestAttemptId);
        setIncludeSpeaking(d.includeSpeaking);
        setIsResumeMode(true);
        setResumeContextLoaded(true);
        setCompletedSections(d.completedSections);
        setResumeTestOverrides(d.resumeTestOverrides);
        setResumeNextSectionIndex(d.resumeNextSectionIndex);
        setTestStarted(false);
        setCurrentSectionIndex(-1);
      } catch (e) {
        console.error('resume-context failed', e);
      } finally {
        if (!cancelled) {
          setResumeContextLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    groupedTests,
    groupId,
    navigate,
    continuePrevious,
    completionStatus,
    stateResumeFullId,
    prefetchedResumeContext,
    prefetchedResumeMatchesAttempt,
  ]);

  // Fetch full section payload (getTestById) whenever the active section has no cache entry
  useEffect(() => {
    if (testStarted && currentSectionIndex >= 0 && currentSectionIndex < sortedTests.length) {
      const test = sortedTests[currentSectionIndex];
      if (test && test.id && !testsWithDetails[test.id]) {
        fetchTestDetails(test.id);
      }
    }
  }, [testStarted, currentSectionIndex, sortedTests, testsWithDetails, fetchTestDetails]);

  const handleStartTest = () => {
    if (isResumeFlow && resumeContextLoaded) {
      const resolvedNext =
        resumeNextSectionIndex != null && resumeNextSectionIndex >= 0
          ? resumeNextSectionIndex
          : sortedTests.findIndex((_, idx) => !completedSections.includes(idx));
      const safeNext =
        resolvedNext >= 0 && !completedSections.includes(resolvedNext)
          ? resolvedNext
          : sortedTests.findIndex((_, idx) => !completedSections.includes(idx));
      setTestStarted(true);
      setCurrentSectionIndex(safeNext >= 0 ? safeNext : 0);
      return;
    }
    setShowSpeakingOption(true);
  };

  const handleConfirmTestStart = () => {
    // Store speaking optional setting in localStorage
    localStorage.setItem('is_speaking_optional', (!includeSpeaking).toString());
    setIsResumeMode(false);
    setResumeTestOverrides(null);
    setShowSpeakingOption(false);
    setTestStarted(true);
    setCurrentSectionIndex(0);
  };

  const handleTestComplete = useCallback(async () => {
    if (groupId) {
      setCompletingFullTest(true);
      try {
        const response = await fetch(`${Base_URL}/app/users/ielts-tests/completeFullTest`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            groupId,
            ...(fullTestAttemptId ? { fullTestAttemptId } : {}),
          }),
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        // Optionally handle response data here
      } catch (err) {
        console.error('Error completing full test:', err);
      } finally {
        setCompletingFullTest(false);
      }
    }
    // Don't close modal or show results here - let useEffect handle it
  }, [groupId, fullTestAttemptId]);

  useEffect(() => {
    return () => {
      if (fullTestFinalizeTimerRef.current != null) {
        clearTimeout(fullTestFinalizeTimerRef.current);
        fullTestFinalizeTimerRef.current = null;
      }
    };
  }, []);

  // Handler for when submission starts (before API call completes)
  const handleSectionSubmitStart = useCallback((sectionIndex) => {
    // Mark section as submitted (used for UI); do not change section index here.
    // Writing/Speaking used to navigate before save finished, which unmounted their UIs and
    // could skip handleSectionComplete / leave testResults incomplete — full-test EndModal then
    // never met its "all sections ready" condition.
    setSubmittedSections((prev) => new Set([...prev, sectionIndex]));
  }, []);

  /** Illusory pause: no API — progress is already saved per section server-side. */
  const handleFullTestPauseClick = useCallback(() => {
    if (completedSections.length === 0) {
      navigate('/ielts-dashboard');
      return;
    }
    setFullTestPauseDialogOpen(true);
  }, [completedSections, navigate]);

  // Handler for when section completes with results (after API call)
  const handleSectionComplete = useCallback(
    (sectionIndex, results) => {
      const sectionIdx = Number(sectionIndex);
      if (!Number.isFinite(sectionIdx) || sectionIdx < 0) {
        console.warn('IELTS full test: invalid section index from onComplete', sectionIndex);
        return;
      }

      const rid = results?.response?.fullTestAttemptId;
      if (rid != null && rid !== '') {
        setFullTestAttemptId(Number(rid));
      }
      setCompletedSections((prev) => {
        if (prev.includes(sectionIdx)) return prev;
        return [...prev, sectionIdx];
      });
      setTestResults((prev) => ({
        ...prev,
        [sectionIdx]: results,
      }));

      const isLastSection = sectionIdx === sortedTests.length - 1;

      if (!isLastSection) {
        setCurrentSectionIndex(sectionIdx + 1);
        return;
      }

      setEndModalOpen(true);
      handleTestComplete();

      // Do not rely on a separate effect + "all sections have scores in memory" — that could fail
      // (resume summary payloads, string vs number indices in completedSections, effect timer cleanup
      // when deps change). When the final section save returns a usable body, leave this UI.
      if (!isIeltsApiSuccessEnvelope(results) && results?.response == null) {
        return;
      }

      if (fullTestFinalizeTimerRef.current != null) {
        clearTimeout(fullTestFinalizeTimerRef.current);
      }
      fullTestFinalizeTimerRef.current = setTimeout(() => {
        fullTestFinalizeTimerRef.current = null;
        setEndModalOpen(false);
        const fid =
          results?.response?.fullTestAttemptId != null && results?.response?.fullTestAttemptId !== ''
            ? Number(results.response.fullTestAttemptId)
            : null;
        navigate('/ielts-results', {
          replace: true,
          state: {
            fromFullTest: true,
            groupId,
            ...(fid != null && Number.isFinite(fid) ? { fullTestAttemptId: fid } : {}),
          },
        });
      }, 1800);
    },
    [sortedTests, handleTestComplete, navigate, groupId]
  );

  const getSectionIcon = (categoryName) => {
    switch (categoryName) {
      case 'Listening':
        return '🎧';
      case 'Reading':
        return '📖';
      case 'Writing':
        return '✍️';
      case 'Speaking':
        return '🎤';
      default:
        return '📝';
    }
  };

  const getTotalDuration = () => {
    const durations = {
      'Listening': 30,
      'Reading': 60,
      'Writing': 60,
      'Speaking': 15
    };
    return sortedTests.reduce((total, test) => {
      return total + (durations[test.category_name] || 0);
    }, 0);
  };

  const renderSpeakingOptionModal = () => (
    <Dialog 
      open={showSpeakingOption} 
      onClose={() => setShowSpeakingOption(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          padding: '8px'
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        color: '#996CFE', 
        fontWeight: 600,
        fontSize: '24px',
        pb: 1
      }}>
        Test Configuration
      </DialogTitle>
      
      <DialogContent sx={{ pb: 1 }}>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ textAlign: 'center', mb: 3 }}>
          Configure your IELTS test experience before starting.
        </Typography>
        
        <Paper sx={{ p: 3, backgroundColor: '#F8F9FA', borderRadius: '8px' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <Typography variant="h4" component="span" mr={2}>🎤</Typography>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Speaking Section
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  15 minutes • Optional
                </Typography>
              </Box>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={includeSpeaking}
                  onChange={(e) => setIncludeSpeaking(e.target.checked)}
                  color="primary"
                />
              }
              label=""
              sx={{ m: 0 }}
            />
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {includeSpeaking 
              ? "Speaking section will be included in your test" 
              : "Speaking section will be skipped"
            }
          </Typography>
        </Paper>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ backgroundColor: '#F0F7FF', p: 2, borderRadius: '8px', border: '1px solid #E3F2FD' }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            <strong>Total Duration:</strong> {Math.floor(getTotalDuration() / 60)}h {getTotalDuration() % 60}m
            <br />
            <strong>Sections:</strong> {sortedTests.map(t => t.category_name).join(', ')}
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 2 }}>
        <Button
          onClick={() => setShowSpeakingOption(false)}
          variant="outlined"
          sx={{ 
            borderColor: '#E0E0E0',
            color: '#666',
            '&:hover': { 
              borderColor: '#996CFE',
              backgroundColor: '#F5F0FF'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirmTestStart}
          variant="contained"
          startIcon={<ArrowForwardIcon />}
          sx={{ 
            backgroundColor: '#996CFE',
            '&:hover': { backgroundColor: '#7C4DFF' },
            px: 3
          }}
        >
          Start Test
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderOverview = () => {
    // Get the actual test name from group_name, fallback to default format
    const testName = groupedTests?.[0]?.group_name || `Complete IELTS Test ${groupId}`;
    
    return (
    <PageContainer maxWidth="lg">
      <TestHeader>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#996CFE', fontWeight: 600 }}>
          {testName}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          This is a complete IELTS test containing all four sections. You will complete each section in order: 
          Listening, Reading, Writing, and Speaking.
        </Typography>
        
        <Box display="flex" alignItems="center" gap={3} mb={3} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1}>
            <TimerIcon sx={{ color: '#996CFE', fontSize: 20 }} />
            <Typography variant="body2" color="text.secondary">
              Total Duration: {Math.floor(getTotalDuration() / 60)}h {getTotalDuration() % 60}m
            </Typography>
          </Box>
          <Chip 
            label={`${sortedTests.length} Sections`} 
            variant="outlined" 
            color="primary" 
          />
          <Chip 
            label={mode === 'simulation' ? 'Simulation Mode' : 'Practice Mode'} 
            color={mode === 'simulation' ? 'warning' : 'success'}
          />
        </Box>
      </TestHeader>

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        {isResumeFlow || isResumeMode ? 'Resume Sections' : 'Test Sections'}
      </Typography>
      {isResumeFlow && resumeContextLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Checking section status and syncing the latest test versions…
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 3,
          mb: 4,
          alignItems: 'flex-start',
        }}
      >
        {isResumeFlow && resumeContextLoading ? (
          [0, 1, 2, 3].map((i) => (
            <Paper
              key={`resume-skel-${i}`}
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '8px',
                border: '1px solid #E0E0E0',
                width: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
              }}
            >
              <Skeleton variant="rounded" width={40} height={40} sx={{ mb: 2 }} />
              <Skeleton variant="text" width="55%" height={28} />
              <Skeleton variant="text" width="35%" />
              <Skeleton variant="text" width="70%" sx={{ mt: 2 }} />
            </Paper>
          ))
        ) : (
          allSortedTests.map((test, index) => (
            <SectionCard
              key={test.id}
              completed={(() => {
                const idxInRun = sortedTests.findIndex((t) => t.id === test.id);
                return idxInRun >= 0 && completedSections.includes(idxInRun);
              })()}
              active={sortedTests[currentSectionIndex]?.id === test.id}
              sx={{
                opacity: !includeSpeaking && test.category_name === 'Speaking' ? 0.5 : 1,
                position: 'relative',
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box display="flex" alignItems="flex-start" mb={0.5}>
                  <Typography variant="h5" component="span" mr={2}>
                    {getSectionIcon(test.category_name)}
                  </Typography>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                      {test.category_name}
                      {test.category_name === 'Speaking' && (
                        <Chip 
                          label="Optional" 
                          size="small" 
                          color="warning" 
                          sx={{ ml: 1, fontSize: '10px', height: '16px' }}
                        />
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {test.category_name === 'Listening' ? '30 min' :
                       test.category_name === 'Reading' ? '60 min' :
                       test.category_name === 'Writing' ? '60 min' : '15 min'}
                    </Typography>
                  </Box>
                </Box>

                {!includeSpeaking && test.category_name === 'Speaking' && (
                  <Box 
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      borderRadius: '8px'
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Skipped
                    </Typography>
                  </Box>
                )}
              </Box>

              {(() => {
                const idxInRun = sortedTests.findIndex((t) => t.id === test.id);
                const isCompleted = idxInRun >= 0 && completedSections.includes(idxInRun);
                const isResumeHere =
                  isResumeMode && idxInRun >= 0 && idxInRun === resumeNextSectionIndex && !isCompleted;
                return (
                  <Box
                    sx={{
                      pt: 0.75,
                      minHeight: 30,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {isCompleted ? (
                      <>
                        <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 18, mr: 0.75, flexShrink: 0 }} />
                        <Typography variant="body2" color="success.main" component="span">
                          Completed
                        </Typography>
                      </>
                    ) : isResumeHere ? (
                      <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                        Resume here
                      </Typography>
                    ) : null}
                  </Box>
                );
              })()}
            </SectionCard>
          ))
        )}
      </Box>

      <Box textAlign="center">
        <StartButton
          onClick={handleStartTest}
          startIcon={<ArrowForwardIcon />}
          size="large"
          disabled={isResumeFlow && (resumeContextLoading || !resumeContextLoaded)}
        >
          {isResumeFlow || isResumeMode ? 'Resume Test' : 'Configure & Start Test'}
        </StartButton>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {isResumeFlow || isResumeMode
            ? 'Continue your in-progress attempt from the next pending section'
            : 'Choose whether to include the speaking section'}
        </Typography>
      </Box>
    </PageContainer>
    );
  };

  const renderCurrentSection = () => {
    if (currentSectionIndex < 0 || currentSectionIndex >= sortedTests.length) {
      return renderOverview();
    }

    const baseTest = sortedTests[currentSectionIndex];
    const detailCached = Boolean(baseTest?.id && testsWithDetails[baseTest.id]);
    const currentTest = testsWithDetails[baseTest.id] || baseTest;

    // Until getTestById is cached, groupedTests may only have resume-context summaries — show explicit status
    const awaitingFullSectionPayload = Boolean(baseTest?.id) && !detailCached;
    if (awaitingFullSectionPayload) {
      const sectionLabel = baseTest.category_name || 'this section';
      const loadFailed =
        sectionDetailLoadError && String(sectionDetailLoadError.testId) === String(baseTest.id);
      return (
        <>
          <Navbar />
          <PageContainer maxWidth="sm">
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: '12px',
                border: '1px solid #E8E0F5',
                backgroundColor: '#FAF8FF',
                boxShadow: 'none',
              }}
            >
              <Typography variant="overline" sx={{ color: '#996CFE', fontWeight: 700, letterSpacing: 0.08 }}>
                Full test · {sectionLabel}
              </Typography>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: '#996CFE', mt: 0.5, mb: 2 }}>
                {loadFailed ? `Couldn’t open ${sectionLabel}` : `Preparing your ${sectionLabel} section`}
              </Typography>
              {loadFailed ? (
                <>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.65 }}>
                    {sectionDetailLoadError.message}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => fetchTestDetails(baseTest.id)}
                    sx={{ mb: 2, backgroundColor: '#996CFE', '&:hover': { backgroundColor: '#7C4DFF' } }}
                  >
                    Try again
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.65 }}>
                    We are loading this part of your test from the server: tasks, instructions, and any audio or
                    passages. If you had already started this section, your saved work is included so you can pick
                    up where you left off.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                    This usually takes a few seconds. Please stay on this page. If nothing appears after a long wait,
                    check your connection and try again from the resume overview.
                  </Typography>
                </>
              )}
              {!loadFailed && (
                <LinearProgress
                  aria-label="Loading section content"
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    backgroundColor: 'rgba(153, 108, 254, 0.15)',
                    '& .MuiLinearProgress-bar': { borderRadius: 999, backgroundColor: '#996CFE' },
                  }}
                />
              )}
            </Paper>
          </PageContainer>
        </>
      );
    }

    return (
      <Box sx={{ minHeight: '100vh' }}>
        {/* Test Content */}
        <Box>
          {currentTest.category_name === 'Listening' && (
            <ListeningTestUI
              mode="simulation"
              timeLimit={30}
              sections={currentTest.sections}
              audioUrl={currentTest.audio_url}
              testId={currentTest.id}
              onComplete={(results) => handleSectionComplete(currentSectionIndex, results)}
              showSaveExit={completedSections.length > 0}
              {...(groupId ? { groupId, fullTestAttemptId, onFullTestPause: handleFullTestPauseClick } : {})}
            />
          )}
          {currentTest.category_name === 'Reading' && (
            <ReadingTestUI
              timeLimit={60}
              sections={currentTest.sections}
              testId={currentTest.id}
              onComplete={(results) => handleSectionComplete(currentSectionIndex, results)}
              showSaveExit={completedSections.length > 0}
              {...(groupId ? { groupId, fullTestAttemptId, onFullTestPause: handleFullTestPauseClick } : {})}
            />
          )}
          {currentTest.category_name === 'Writing' && (
            <WritingTestUI
              mode="test"
              timeLimit={60}
              sections={currentTest.sections}
              testId={currentTest.id}
              onSubmitStart={() => handleSectionSubmitStart(currentSectionIndex)}
              onComplete={(results) => handleSectionComplete(currentSectionIndex, results)}
              showSaveExit={completedSections.length > 0}
              testType={currentTest.test_type}
              {...(groupId ? { groupId, fullTestAttemptId, onFullTestPause: handleFullTestPauseClick } : {})}
            />
          )}
          {currentTest.category_name === 'Speaking' && (
            <SpeakingTestUI
              mode="test"
              timeLimit={15}
              sections={currentTest.sections}
              testId={currentTest.id}
              onSubmitStart={() => handleSectionSubmitStart(currentSectionIndex)}
              onComplete={(results) => handleSectionComplete(currentSectionIndex, results)}
              showSaveExit={completedSections.length > 0}
              {...(groupId ? { groupId, fullTestAttemptId, onFullTestPause: handleFullTestPauseClick } : {})}
            />
          )}
        </Box>
      </Box>
    );
  };

  if (!groupedTests || !groupId) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (showFullTestResults) {
    return (
      <PageContainer maxWidth="md">
        <TestHeader>
          <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#996CFE', fontWeight: 600 }}>
            Full Test Results Summary
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Here are your results for each section of the full IELTS test.
          </Typography>
        </TestHeader>
        <Box>
          {sortedTests.map((test, idx) => (
            <Paper key={test.id} sx={{ p: 3, borderRadius: '12px', boxShadow: '0px 4px 12px rgba(0,0,0,0.05)', mb: 3 }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Typography variant="h5">{getSectionIcon(test.category_name)}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{test.category_name}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Section ID: {test.id}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Time Limit: {test.category_name === 'Listening' ? '30 min' : test.category_name === 'Reading' ? '60 min' : test.category_name === 'Writing' ? '60 min' : '15 min'}
              </Typography>
              <Divider sx={{ my: 1 }} />
              {(() => {
                const result = testResults[idx];
                const scoreAvailable = shouldShowScore(result);
                const isPending = result?.response?.status === 'pending_evaluation';
                const hasAI = hasAIScore(result);
                
                if (['Writing', 'Speaking'].includes(test.category_name)) {
                  if (scoreAvailable || hasAI) {
                    // Show band score only for Writing/Speaking (no correct/incorrect, no question breakdown)
                    return (
                      <>
                        <IELTSBandScore 
                          apiResult={result} 
                          showCorrectIncorrect={false}
                          showOnlyWhenScoreAvailable={true}
                        />
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            View detailed scoring and feedback on the test details page.
                          </Typography>
                        </Box>
                      </>
                    );
                  } else if (isPending) {
                    return (
                      <Typography variant="body1" color="info.main" sx={{ fontWeight: 500, mt: 2 }}>
                        Results are pending evaluation. Please check back shortly.
                      </Typography>
                    );
                  } else {
                    return (
                      <Typography variant="body1" color="warning.main" sx={{ fontWeight: 500, mt: 2 }}>
                        Results for Writing and Speaking will be available in the results dashboard.
                      </Typography>
                    );
                  }
                } else {
                  if (!result && completedSections.includes(idx)) {
                    return (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, lineHeight: 1.65 }}>
                        This section was finished in a saved session before this run. Open{' '}
                        <strong>IELTS results</strong> or the section&apos;s test details for full scores and
                        feedback.
                      </Typography>
                    );
                  }
                  return <SectionResultSummary result={result} />;
                }
              })()}
            </Paper>
          ))}
        </Box>
        <Box textAlign="center" mt={4}>
          <Button variant="contained" color="primary" onClick={() => navigate('/ielts-results')}>
            Go to Results Overview
          </Button>
        </Box>
      </PageContainer>
    );
  }

  return (
    <>
      {!testStarted && <Navbar />}
      {testStarted ? renderCurrentSection() : renderOverview()}
      {renderSpeakingOptionModal()}
      {/* EndModal for full test - shows once at the end */}
      {testStarted && (
        <EndModal 
          open={endModalOpen} 
          onClose={() => {}} // Don't allow manual close during full test
          sentences={IELTS_FULL_TEST_SENTENCES} 
          intervalMs={3000}
          testTitle="IELTS Full Test"
          navigateTo="/ielts-results"
        />
      )}
      <Dialog
        open={fullTestPauseDialogOpen}
        onClose={() => setFullTestPauseDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ 
          sx: { 
            borderRadius: '16px',
            p: { xs: 1, sm: 2 }
          } 
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          gap: 1,
          pt: 3,
          pb: 1,
          position: 'relative'
        }}>
          <Box sx={{ 
            width: 64, 
            height: 64, 
            borderRadius: '50%', 
            backgroundColor: '#F5F0FF', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            mb: 1
          }}>
            <PauseCircleOutlineIcon sx={{ fontSize: 36, color: '#996CFE' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1A1A1A' }}>
            Pause Full Test
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setFullTestPauseDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', px: 3, pb: 3 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
            Your progress is automatically saved after each completed section.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            You can resume this test later from the dashboard, or view results for your completed sections.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', gap: 1.5, alignItems: 'stretch' }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              setFullTestPauseDialogOpen(false);
              navigate('/ielts-dashboard');
            }}
            sx={{ 
              textTransform: 'none', 
              backgroundColor: '#996CFE', 
              '&:hover': { backgroundColor: '#7C4DFF' },
              py: 1.2,
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: '8px',
              boxShadow: 'none'
            }}
          >
            Save & Go to Dashboard
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => {
              setFullTestPauseDialogOpen(false);
              navigate('/ielts-results');
            }}
            sx={{ 
              textTransform: 'none', 
              borderColor: '#E0E0E0', 
              color: '#424242',
              py: 1.2,
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: '8px',
              '&:hover': {
                borderColor: '#996CFE',
                backgroundColor: '#F5F0FF',
                color: '#996CFE'
              }
            }}
          >
            View Results
          </Button>
          <Button 
            fullWidth
            onClick={() => setFullTestPauseDialogOpen(false)} 
            sx={{ 
              textTransform: 'none',
              color: '#757575',
              fontWeight: 500,
              mt: 0.5,
              '&:hover': { backgroundColor: 'transparent', color: '#424242' }
            }}
          >
            Cancel & Resume Test
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IELTSFullTestPage;